import React, { useRef, useState } from "react";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { sendApplicationEmail } from "../leadEmail";
import { Link } from "react-router-dom";
import './Apply.css';

const DISQ_FEATURES = [
  { id: 'payments',  label: '💳 Online payments / checkout' },
  { id: 'accounts',  label: '🔐 User accounts / login' },
  { id: 'messaging', label: '💬 Messaging or live chat' },
  { id: 'booking',   label: '📅 Booking / scheduling system' },
  { id: 'ecom',      label: '🛒 E-commerce store' },
];

const YES_NO_UNSURE = [
  { id: 'yes',    label: 'Yes' },
  { id: 'no',     label: 'No' },
  { id: 'unsure', label: 'Not sure' },
];

const PAGE_OPTIONS = [
  { label: '1–3 pages', value: 3 },
  { label: '4–6 pages', value: 6 },
  { label: '7–10 pages', value: 10 },
  { label: '10+ pages',  value: 15 },
];

const CLIENTS = [
  'June Health', 'Global Automation Technology', 'Snap Carts', 'Super Trax & Dirt Trax',
  'Next Play Hockey', 'FitVault', 'LecturePal', 'Open Road Systems',
  'Bulletproof Interviewing', 'RFR Pro', 'Classic Fence & Deck', 'Robinson BnB',
  'WTF Burger Company', 'Salt N Pepper Eats',
];

// Real reviews go here: { name, business, text }
const REAL_REVIEWS = [];

// SAMPLE reviews for previewing the layout. Fictional people and businesses,
// so they only render on the local dev server (npm start), never in production.
const SAMPLE_REVIEWS = [
  { name: 'Dana K.',    business: 'Lakeside Pet Grooming, Grimsby',      text: 'Had a site up in a week and I didn\'t pay a cent for the build. Customers keep telling me how easy it is to find us now.' },
  { name: 'Mike R.',    business: 'Riverbend Landscaping, Fergus',        text: 'Matt was quick, honest, and actually listened. The site looks better than ones my competitors paid thousands for.' },
  { name: 'Priya S.',   business: 'Elora Yoga Loft, Elora',               text: 'I was nervous about the tech side but Matt handled everything. Class sign-ups went up the first month.' },
  { name: 'Tom B.',     business: 'Dover Bait & Tackle, Port Dover',      text: 'Simple, clean, and works great on phones, which is where all my customers find me. Couldn\'t be happier.' },
  { name: 'Jen L.',     business: 'Prince Edward Bakehouse, Picton',      text: 'Fast turnaround and super easy to work with. Matt even set up my domain so I never had to think about it.' },
  { name: 'Carlos M.',  business: 'Blue Mountain Auto Detailing, Collingwood', text: 'Started getting calls from Google within a couple weeks. Best decision I made for the business this year.' },
  { name: 'Heather W.', business: 'Rideau Home Cleaning, Smiths Falls',   text: 'Professional from start to finish. He made changes the same day I asked. Highly recommend.' },
  { name: 'Greg T.',    business: 'Paris Barber Co., Paris',              text: 'Looks sharp and loads fast. People book online now instead of calling all day. Huge time saver.' },
  { name: 'Amanda F.',  business: 'Sunparlour Flowers, Kingsville',       text: 'I\'d put off getting a website for years. Wish I\'d done it sooner. Matt made it painless.' },
  { name: 'Kevin D.',   business: 'Muskoka Dock Repair, Bracebridge',     text: 'Straight shooter, no upsell games. Site does exactly what I need and customers find me easily.' },
  { name: 'Laura P.',   business: 'Huron Shores Café, Goderich',          text: 'Our menu and hours are finally online and look great. Tourists find us way more often now.' },
  { name: 'Steve H.',   business: 'Kawartha Roofing, Lindsay',            text: 'Great communication the whole way through. The site brings in quote requests every week.' },
];

const REVIEWS = process.env.NODE_ENV === 'production'
  ? REAL_REVIEWS
  : [...REAL_REVIEWS, ...SAMPLE_REVIEWS];

function qualify(pages, disqSelected, description) {
  const pageNum = parseInt(pages) || 99;
  const descOk  = description.trim().length >= 25;
  return pageNum <= 6 && disqSelected.length === 0 && descOk;
}

export default function Apply() {
  const [name,         setName]         = useState('');
  const [email,        setEmail]        = useState('');
  const [phone,        setPhone]        = useState('');
  const [pages,        setPages]        = useState('');
  const [description,  setDescription]  = useState('');
  const [disqSelected, setDisqSelected] = useState([]);
  const [hasDomain,    setHasDomain]    = useState('');
  const [hasHosting,   setHasHosting]   = useState('');
  const [hostingProvider, setHostingProvider] = useState('');

  const [step,       setStep]       = useState(1);
  const [qualified,  setQualified]  = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors,     setErrors]     = useState({});

  const resultRef = useRef(null);

  const toggleDisq = (id) =>
    setDisqSelected(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );

  const validate = () => {
    const e = {};
    if (!name.trim())
      e.name = 'Name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = 'Enter a valid email address.';
    if (phone.replace(/\D/g, '').length < 10)
      e.phone = 'Enter a valid phone number.';
    if (!pages)
      e.pages = 'Please select how many pages you need.';
    if (description.trim().length < 25)
      e.description = 'Please describe your project (25 characters minimum).';
    if (!hasDomain)
      e.hasDomain = 'Please choose an option.';
    if (!hasHosting)
      e.hasHosting = 'Please choose an option.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);

    const isQualified = qualify(pages, disqSelected, description);

    const provider = hasHosting === 'yes' ? hostingProvider.trim() : '';

   const data = {
  name, email, phone, pages, description,
  disqSelected,
  hasDomain, hasHosting,
  hostingProvider: provider,
  qualified:   isQualified,
  status:      'pending',
  completed:   false,
  createdAt:   Timestamp.now(),
  source:      'free_website',
};

    try {
      await addDoc(collection(db, 'applications'), data);

      if (isQualified) {
        await addDoc(collection(db, 'qualified_leads'), {
          name, email, phone,
          source:    'free_website_apply',
          createdAt: Timestamp.now(),
        });
      }

      await sendApplicationEmail({
        name, email, phone, pages, description, disqSelected,
        hasDomain, hasHosting, hostingProvider: provider,
        qualified: isQualified,
        pageLabel: PAGE_OPTIONS.find(o => String(o.value) === String(pages))?.label || pages,
        featureLabels: Object.fromEntries(DISQ_FEATURES.map(f => [f.id, f.label])),
      });

      if (window.fbq) window.fbq('track', 'SubmitApplication');

      setQualified(isQualified);
      setStep(2);
      setTimeout(() =>
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100
      );
    } catch (err) {
      console.error(err);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const descLen = description.trim().length;

  return (
    <div className="apply-page">

      {/* ── HERO ── */}
      <div className="apply-hero">
        <div className="hero-inner">
          <div className="hero-badge">🎁 Limited Spots Available</div>
          <h1>Free Website for <em>Qualified</em> Businesses</h1>
          <p>
            We build free, professional websites for qualifying small businesses across Ontario.
            Takes 2 minutes to apply. Find out instantly if you qualify.
          </p>
          <div className="hero-social-proof">
            <div className="proof-item"><strong>★★★★★</strong> 5.0 Google Reviews</div>
            <div className="proof-dot" />
            <div className="proof-item"><strong>90+</strong> websites built</div>
            <div className="proof-dot" />
            <div className="proof-item"><strong>$0</strong> cost to you</div>
          </div>
        </div>
      </div>

    

      <div className="apply-main">

        {/* ── STEP 1: FORM ── */}
        {step === 1 && (
          <div className="apply-form-card">
            <div className="form-card-title">Apply Here</div>
            <div className="form-card-sub">
              We accept ~67% of applicants. Get an instant decision in under 30 seconds.
            </div>

            <form onSubmit={handleSubmit} noValidate>

              <div className="form-field">
                <label className="field-label">Your Name</label>
                <input className="field-input" type="text" placeholder="First and last name"
                  value={name} onChange={e => setName(e.target.value)} />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>

              <div className="form-field">
                <label className="field-label">Email Address</label>
                <input className="field-input" type="email" placeholder="you@yourbusiness.com"
                  value={email} onChange={e => setEmail(e.target.value)} />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>

              <div className="form-field">
                <label className="field-label">Phone Number</label>
                <input className="field-input" type="tel" autoComplete="tel" placeholder="(905) 555-1234"
                  value={phone} onChange={e => setPhone(e.target.value)} />
                {errors.phone && <div className="field-error">{errors.phone}</div>}
                <div className="field-privacy">We'll only use this to contact you about your website.</div>
              </div>

              <div className="form-field">
                <label className="field-label">How many pages do you need?</label>
                <select className="field-select" value={pages}
                  onChange={e => setPages(e.target.value)}>
                  <option value="">Select...</option>
                  {PAGE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {errors.pages && <div className="field-error">{errors.pages}</div>}
              </div>

              <div className="form-field">
                <label className="field-label">What do you want built?</label>
                <textarea className="field-textarea" rows={3}
                  placeholder="Describe your website — what it's for, who it's for, what pages you need."
                  value={description} onChange={e => setDescription(e.target.value)} />
                <div className={`char-count ${descLen >= 25 ? 'ok' : 'bad'}`}>
                  {descLen}/25 min
                </div>
                {errors.description && <div className="field-error">{errors.description}</div>}
              </div>

              {/* SINGLE DISQUALIFIER BLOCK */}
              <div className="disq-section">
                <div className="disq-title">Does your site need any of the following?</div>
                <div className="disq-hint">Select all that apply, leave blank if none.</div>
                <div className="disq-chips">
                  {DISQ_FEATURES.map(f => (
                    <button type="button" key={f.id}
                      className={`disq-chip${disqSelected.includes(f.id) ? ' selected' : ''}`}
                      onClick={() => toggleDisq(f.id)}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* DOMAIN & HOSTING */}
              <div className="disq-section">
                <div className="disq-title">Domain & Hosting</div>

                <div className="choice-group">
                  <div className="choice-label">
                    Do you already own a domain name? <span>(e.g. yourbusiness.com)</span>
                  </div>
                  <div className="disq-chips">
                    {YES_NO_UNSURE.map(o => (
                      <button type="button" key={o.id}
                        className={`disq-chip${hasDomain === o.id ? ' selected' : ''}`}
                        onClick={() => setHasDomain(o.id)}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  {errors.hasDomain && <div className="field-error">{errors.hasDomain}</div>}
                </div>

                <div className="choice-group">
                  <div className="choice-label">
                    Do you have hosting? <span>(where your website lives online)</span>
                  </div>
                  <div className="disq-chips">
                    {YES_NO_UNSURE.map(o => (
                      <button type="button" key={o.id}
                        className={`disq-chip${hasHosting === o.id ? ' selected' : ''}`}
                        onClick={() => setHasHosting(o.id)}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  {hasHosting === 'yes' && (
                    <input className="field-input choice-input" type="text"
                      placeholder="Who with? (optional, e.g. GoDaddy, Wix, Squarespace)"
                      value={hostingProvider} onChange={e => setHostingProvider(e.target.value)} />
                  )}
                  {errors.hasHosting && <div className="field-error">{errors.hasHosting}</div>}
                </div>

                <div className="choice-reassure">
                  ✓ Don't worry, we can handle your domain and hosting for you too.
                </div>
              </div>

              <button className="submit-btn" type="submit" disabled={submitting}>
                {submitting
                  ? 'Checking your application...'
                  : 'Yes, Sign Me Up! →'}
              </button>
              <p className="form-privacy">Applying multiple times does not help your application.</p>

            </form>
          </div>
        )}

        {/* ── STEP 2: RESULT ── */}
        {step === 2 && (
          <div ref={resultRef}>

            {qualified && (
              <div className="outcome-card qualified">
                <div className="outcome-icon">🎉</div>
                <h2>You qualify for a free website build.</h2>
              
                <p className="booking-note-top">
                  ⚡ We will call you as soon as possible, typically within 2–3 business days.
                </p>
              </div>
            )}

            {!qualified && (
              <div className="outcome-card not-qualified">
                <div className="outcome-icon">👋</div>
                <h2>Your project needs a custom build, but we can still help.</h2>
                <p>
                  Your project has some advanced requirements that go beyond our free program.{' '}
                  <strong>We've saved your details</strong> and would love to build something
                  great together at a competitive rate.
                </p>
                <div className="not-qual-confirm">
                  ✓ Your application has been received.<br />
                  Book a free 15-minute call and we'll put together a custom quote, no obligation. Please use or cost estimator on the pricing page.
                </div>
               
          
                <a className="btn-dark"
                  href="/pricing"
                  target="_blank" rel="noopener noreferrer">
                  Pricing Page Estimator →
                </a>
              </div>
              
            )}

          </div>
        )}
      </div>

      {/* ── GUARANTEE ── */}
      <div className="apply-guarantee">
        <div className="guarantee-seal">100%</div>
        <div>
          <h3>Satisfaction Promise</h3>
          <p>
            We keep refining your website until you love it. No hard feelings, no hoops.
          </p>
        </div>
      </div>

      {/* ── CLIENTS ── */}
      <div className="apply-clients">
        <div className="apply-clients-title">Trusted by businesses like</div>
        <div className="apply-clients-list">
          {CLIENTS.map(c => <span key={c} className="client-chip">{c}</span>)}
        </div>
      </div>

      {/* ── REVIEWS ── */}
      {REVIEWS.length > 0 && (
      <div className="apply-reviews">
        <div className="apply-reviews-head">
          <div className="apply-reviews-stars">★★★★★</div>
          <h3>What Our Clients Say</h3>
        </div>
        <div className="apply-reviews-grid">
          {REVIEWS.map((r, i) => (
            <div key={i} className="apply-review">
              <div className="apply-review-stars">★★★★★</div>
              <p className="apply-review-text">"{r.text}"</p>
              <div className="apply-review-name">{r.name}</div>
              <div className="apply-review-biz">{r.business}</div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* ── TRUST ── */}
      <div className="apply-trust">
        <div className="trust-inner">
          <h3>Why Businesses Trust DuDigital</h3>
          <ul className="trust-list">
            <li>Earn instant trust and credibility online</li>
            <li>Be discoverable anytime, anywhere — 24/7</li>
            <li>Attract more leads and close more sales</li>
            <li>Stand out from competitors with no website</li>
            <li>Grow without any upfront investment</li>
            <li>
              <span>
                Built by a real person in Ontario — not a template factory.{' '}
                <Link to="/about" className="trust-link">Meet Matt →</Link>
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="apply-faq">
        <h3>Frequently Asked Questions</h3>
        {[
          { q: '"Why is it free?"', a: 'We build free websites to grow our referral network and give small businesses a risk-free way to get online. Many clients return for bigger projects so it\'s a genuine win-win.' },
          { q: '"What\'s the catch?"', a: 'There\'s no catch. We build the site for free; hosting and domain options are discussed on our call so you get what fits your business.' },
          { q: '"Will it look professional?"', a: 'Absolutely. You get the same quality as our paying clients- custom-designed to help you look credible and attract customers.' },
          { q: '"How long does it take?"', a: 'Most websites are ready within 7 days of your kickoff call.' },
          { q: '"What if I need changes later?"', a: 'We\'re always here. You can upgrade anytime or request updates as your business grows.' },
        ].map((faq, i) => (
          <div key={i} className="faq-item">
            <div className="faq-q">{faq.q}</div>
            <div className="faq-a">{faq.a}</div>
          </div>
        ))}
      </div>

    </div>
  );
}